import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Checkbox from '../../components/Checkbox/Checkbox';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import Loading from '../../components/Loading/Loading';
import SubmitButton from '../../components/SubmitButton/SubmitButton';
import Modal from '../../components/Modal/Modal';

import PrivacyPolicy from '../PrivacyPolicy/PrivacyPolicy';
import TermsOfService from '../TermsOfService/TermsOfService';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';

import emailRegex from '../../utils/emailRegex';
import onlyNumbers from '../../utils/onlyNumbers';

import DefaultAvatar from '../../assets/svgs/DefaultAvatar';
import EmailIcon from '../../assets/svgs/EmailIcon';
import Logo from '../../assets/svgs/Logo';
import PadlockIcon from '../../assets/svgs/PadlockIcon';
import registerIllustration from '../../assets/images/healthscan-register-illustration.svg';
import { cpfMask } from '../../utils/cpfMask';
import { RegisterStyle } from './RegisterStyle';
import { t } from 'i18n-js';

const Register = () => {
  const language = useSelector((state) => state.settings.language);
  const history = useHistory();

  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [acceptsTermsError, setAcceptsTermsError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const verifyFirstName = (value) => {
    if (value && value.length) {
      return true;
    }

    setFirstNameError(translate('firstNameRequired', language));

    return false;
  };

  const verifyLasttName = (value) => {
    if (value && value.length) {
      return true;
    }

    setLastNameError(translate('lastNameRequired', language));

    return false;
  };

  const verifyEmail = (value) => {
    if (emailRegex.test(String(value).toLowerCase())) {
      return true;
    }

    setEmailError(translate('validEmailRequired', language));

    return false;
  };

  const verifyPassword = (value, repeatValue) => {
    if (value.length && repeatValue.length) {

      const minLength = 8;
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*.()]/.test(value);
      const isValidLength = value.length >= minLength;
    
      if (!isValidLength) {
        setPasswordError(translate('passwordMinLength', language));
        return false;
      }
      if (!hasUpperCase) {
        setPasswordError(translate('passwordUppercase', language));
        return false;
      }
      if (!hasLowerCase) {
        setPasswordError(translate('passwordLowercase', language));
        return false;
      }
      if (!hasNumber) {
        setPasswordError(translate('passwordNumber', language));
        return false;
      }
      if (!hasSpecialChar) 
      {
        setPasswordError(translate('passwordSpecialChar', language))
        return false;
      }

      if (value === repeatValue) {
        return true;
      }

      setPasswordError(translate('passwordsDoNotMatch', language));

      return false;
    }

    setPasswordError(translate('passwordRequired', language));

    return false;
  };

  const verifyCpf = (value) => {
    const cpfonlyNumbers = onlyNumbers(value);

    if (cpfonlyNumbers.length === 11) {
      setCpf(cpfonlyNumbers);

      return true;
    }

    setCpfError(translate('validCpfRequired', language));

    return false;
  };

  const verifyAgreeRules = (value) => {
    if (value) {
      return true;
    }

    setAcceptsTermsError(translate('acceptanceRequired', language));

    return false;
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    setLoading(true);
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPasswordError('');
    setCpfError('');
    setAcceptsTermsError('');

    const isValidFirstName = verifyFirstName(firstName);
    const isValidLasttName = verifyLasttName(lastName);
    const isValidEmail = verifyEmail(email);
    const isValidPassword = verifyPassword(password, repeatPassword);
    const isValidCpf = verifyCpf(onlyNumbers(cpf));
    const isValidAgreeRules = verifyAgreeRules(acceptsTerms);

    const onlyNumbersCpf = onlyNumbers(cpf);

    if (
      isValidFirstName &&
      isValidLasttName &&
      isValidEmail &&
      isValidPassword &&
      isValidCpf &&
      isValidAgreeRules
    ) {
      const registerResult = await Api.registerUser({
        name: `${firstName} ${lastName}`,
        email,
        password,
        onlyNumbersCpf,
        accepts_usage_terms: acceptsTerms,
        accepts_privacy_policy: acceptsTerms,
      });

      if (
        registerResult &&
        registerResult.status &&
        registerResult.status === 201
      ) {
        setLoading(false);
        history.push('/entrar');
      } else if (
        registerResult &&
        registerResult.status &&
        registerResult.status === 400
      ) {
        if (registerResult.data && registerResult.data.name) {
          if (
            registerResult.data.name.includes('This field may not be blank.')
          ) {
            setFirstNameError('Nome obrigatório');
            setLastNameError('Sobrenome obrigatório');
          }
        }

        if (registerResult.data && registerResult.data.email) {
          if (
            registerResult.data.email.includes('This field may not be blank.')
          ) {
            setEmailError('E-mail obrigatório');
          } else if (
            registerResult.data.email.includes('Enter a valid email address.')
          ) {
            setEmailError('E-mail inválido');
          } else if (
            registerResult.data.email.includes(
              'Usuário with this email already exists.'
            )
          ) {
            setEmailError('E-mail já cadastrado');
          }
        }

        if (registerResult.data && registerResult.data.password) {
          if (
            registerResult.data.password.includes(
              'This field may not be blank.'
            )
          ) {
            setPasswordError('Senha obrigatória');
          }
        }

        if (registerResult.data && registerResult.data.cpf) {
          if (
            registerResult.data.cpf.includes('This field may not be blank.')
          ) {
            setCpfError('CPF obrigatório');
          } else if (
            registerResult.data.cpf.includes(
              'Cpf deve conter somente 11 números.'
            )
          ) {
            setCpfError('CPF inválido');
          } else if (registerResult.data.cpf.includes('Cpf must be unique')) {
            setCpfError('CPF já cadastrado');
          }
        }

        if (registerResult.data && registerResult.data.accepts_usage_terms) {
          if (
            registerResult.data.accepts_usage_terms.includes(
              'You must accept the usage terms and conditions.'
            )
          ) {
            setAcceptsTermsError('Aceite obrigatório');
          }
        }

        if (registerResult.data && registerResult.data.accepts_usage_terms) {
          if (
            registerResult.data.accepts_usage_terms.includes(
              'You must accept the usage terms and conditions.'
            )
          ) {
            setAcceptsTermsError('Aceite obrigatório');
          }
        }

        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  return (
    <RegisterStyle>
      <Header />
      <div className="register-content">
        <section className="image-section">
          <div className="logo-wrapper">
            <Logo theme="white" />
          </div>
          <img
            className="register-illustration"
            src={registerIllustration}
            alt="Register illustration"
          />
        </section>

        <section className="form-section">
          <div className="form-content">
            <div className="form-head">
              <h1 className="form-head-title">{translate('firstAcess', language)}</h1>
              <p className="form-head-description">
              {translate('helloWelcome', language)}
                <br />
              {translate('enterInformation', language)}
              </p>
            </div>
            <form onSubmit={handleRegister}>
              <div className="form-row">
                <div className="form-column">
                  <InputField
                    type="text"
                    placeholder={translate('typeYourName', language)}
                    inputName="first-name"
                    value={firstName}
                    icon={<DefaultAvatar />}
                    error={firstNameError}
                    onChange={setFirstName}
                  />
                </div>
                <div className="form-column">
                  <InputField
                    type="text"
                    placeholder={translate('typeYourLastName', language)}
                    inputName="last-name"
                    value={lastName}
                    error={lastNameError}
                    onChange={setLastName}
                  />
                </div>
              </div>
              <InputField
                type="text"
                placeholder={translate('typeYourConstitutionalEmail', language)}
                inputName="email"
                value={email}
                icon={<EmailIcon />}
                error={emailError}
                onChange={setEmail}
              />
              <InputField
                type="text"
                placeholder={translate('typeYourCPF', language)}
                inputName="cpf"
                value={cpfMask(cpf)}
                error={cpfError}
                onChange={setCpf}
              />
              <InputField
                type="password"
                placeholder={translate('createAPassword', language)}
                inputName="password"
                value={password}
                icon={<PadlockIcon />}
                theme={passwordError.length ? 'error' : ''}
                instructions={translate('passwordInstructions', language)}
                onChange={setPassword}
              />
              <InputField
                type="password"
                placeholder={translate('confirmYourPassword', language)}
                inputName="repeat-password"
                value={repeatPassword}
                icon={<PadlockIcon />}
                error={passwordError}
                onChange={setRepeatPassword}
              />
              <Checkbox
                text="Concordar com Termos e Condições de uso e Política de Privacidade e Segurança."
                checked={acceptsTerms}
                inputName="accepts-terms"
                onChange={() => setAcceptsTerms(!acceptsTerms)}
                error={acceptsTermsError}
              >
                {translate('agreeWithTerms', language)}{' '}
                <a
                  href="#" 
                  className="terms-modal-link"
                  onClick={(e) => {
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    setShowTermsModal(true);
                  }}
                  style={{ 
                    fontWeight: 'bold', 
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {translate('agreeWithTermsOfService', language)} 
                </a>
                {language === 'pt-br' ? ' e ' : ' and '}
                <a
                  href="#"
                  className="policy-modal-link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPolicyModal(true);
                  }}
                  style={{ 
                    fontWeight: 'bold', 
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {translate('agreeWithPolicy', language)}
                </a>
                .
              </Checkbox>

              {showTermsModal ?
                <Modal backgroundColor="white" handleClose={() => setShowTermsModal(false)} theme={"light"} withShadow >
                  <TermsOfService/>
                </Modal>
              : null}

              {showPolicyModal ?
                <Modal backgroundColor="white" handleClose={() => setShowPolicyModal(false)} theme={"light"} withShadow >
                  <PrivacyPolicy/>
                </Modal>
              : null}

              <SubmitButton value={translate('continue', language)} onClick={handleRegister} />
            </form>
            <p className="go-to-login">
            {translate('alreadyHaveAccount', language)} <Link to="/entrar">{translate('loginHere', language)}</Link>.
            </p>
          </div>
        </section>
      </div>
      {loading && <Loading />}
    </RegisterStyle>
  );
};

export default Register;
